import { decode } from "he";
import { useCallback, useRef, useState } from "react";
import { OverlayPanel } from "primereact/overlaypanel";
import { SideBarLinkButton } from "./SideBarLinkButton";
import type { NavigationItem } from "./types";

import styles from "../Menu.module.css";

export const SideBarContextLink = ({
  isMinimized,
  navigationItem,
  renderMenu,
}: {
  isMinimized: boolean;
  navigationItem: NavigationItem;
  renderMenu: (
    items: Array<NavigationItem>,
    insideMenu?: boolean,
  ) => JSX.Element[];
}) => {
  const overlayRef = useRef<OverlayPanel>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const pendingFocusRef = useRef<number | null>(null);

  const getMenuItems = useCallback(() => {
    if (!menuRef.current) return [];
    return Array.from(
      menuRef.current.querySelectorAll<HTMLElement>('[role="menuitem"]'),
    );
  }, []);

  const focusMenuItem = useCallback(
    (index: number) => {
      const items = getMenuItems();
      if (items.length === 0) return;
      const safeIndex = ((index % items.length) + items.length) % items.length;
      items[safeIndex]?.focus();
    },
    [getMenuItems],
  );

  const closeMenu = useCallback(() => {
    overlayRef.current?.hide();
    triggerRef.current?.focus();
  }, []);

  const handleShow = useCallback(() => {
    setIsOpen(true);
    if (pendingFocusRef.current !== null) {
      const index = pendingFocusRef.current;
      pendingFocusRef.current = null;
      requestAnimationFrame(() => focusMenuItem(index));
    }
  }, [focusMenuItem]);

  const handleHide = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleTriggerKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          pendingFocusRef.current = 0;
          if (isOpen) {
            focusMenuItem(0);
          } else {
            overlayRef.current?.show(e as never, triggerRef.current);
          }
          break;
        case "ArrowUp":
          e.preventDefault();
          pendingFocusRef.current = -1;
          if (isOpen) {
            focusMenuItem(-1);
          } else {
            overlayRef.current?.show(e as never, triggerRef.current);
          }
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          if (isOpen) {
            overlayRef.current?.hide();
          } else {
            pendingFocusRef.current = 0;
            overlayRef.current?.show(e as never, triggerRef.current);
          }
          break;
        case "Escape":
          if (isOpen) {
            e.preventDefault();
            e.stopPropagation();
            closeMenu();
          }
          break;
      }
    },
    [isOpen, focusMenuItem, closeMenu],
  );

  const handleMenuKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const items = getMenuItems();
      const currentIndex = items.indexOf(e.target as HTMLElement);

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          focusMenuItem(currentIndex + 1);
          break;
        case "ArrowUp":
          e.preventDefault();
          focusMenuItem(currentIndex - 1);
          break;
        case "Home":
          e.preventDefault();
          focusMenuItem(0);
          break;
        case "End":
          e.preventDefault();
          focusMenuItem(items.length - 1);
          break;
        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          closeMenu();
          break;
        case "Tab":
          e.preventDefault();
          closeMenu();
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          (e.target as HTMLElement).click();
          closeMenu();
          break;
      }
    },
    [getMenuItems, focusMenuItem, closeMenu],
  );

  return (
    <>
      <SideBarLinkButton
        ref={triggerRef}
        dataCy={navigationItem.dataCy}
        key={`nav_link_${navigationItem.id}`}
        text={decode(navigationItem.text ?? "")}
        hideText={isMinimized}
        icon={navigationItem.icon ?? ""}
        fillColor={navigationItem.fillColor}
        showTooltip={navigationItem.showTooltip && isMinimized}
        truncateText={navigationItem.truncateText}
        onClick={(e) => overlayRef.current?.toggle(e)}
        onKeyDown={handleTriggerKeyDown}
        ariaHasPopup={true}
        ariaExpanded={isOpen}
      />
      <OverlayPanel
        ref={overlayRef}
        className={styles.popupMenuOverlay}
        pt={{ content: { className: styles.contentPopupMenuOverlay } }}
        data-cy={`overlay-${navigationItem.dataCy}`}
        onShow={handleShow}
        onHide={handleHide}
      >
        <nav aria-label={decode(navigationItem.text ?? "")}>
          <div ref={menuRef} role="menu" onKeyDown={handleMenuKeyDown}>
            {renderMenu(navigationItem.submenuItems ?? [], true)}
          </div>
        </nav>
      </OverlayPanel>
    </>
  );
};
